import {
  Component,
  computed,
  effect,
  Input,
  input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexStroke,
  ApexMarkers,
  ApexYAxis,
  ApexGrid,
  ApexTitleSubtitle,
  ApexLegend,
} from 'ng-apexcharts';
import { NgApexchartsModule } from 'ng-apexcharts';
import { BaseDashboardChartComponent } from '../base-dashboard-chart.component';
import { TimeIntervalDto, TimeIntervalRecord } from '../../../interfaces/reports/time-interval';
import { ChartCardComponent } from '../chart-card/chart-card.component';
import { SpinnerComponent } from '../../spinner/spinner.component';
import {TranslatePipe} from "@ngx-translate/core";


export type ScatterChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  markers: ApexMarkers;
  tooltip: any;
  yaxis: ApexYAxis;
  grid: ApexGrid;
  legend: ApexLegend;
  title: ApexTitleSubtitle;
  colors: string[];
  fill: {
    type: string;
    colors?: string[] | undefined;
  };
};

@Component({
  selector: 'co-scatter-chart',
  templateUrl: './scatter-chart.component.html',
  styleUrls: ['./scatter-chart.component.css'],
  imports: [NgApexchartsModule, ChartCardComponent, SpinnerComponent, TranslatePipe],
})
export class ScatterChartComponent
  extends BaseDashboardChartComponent
  implements OnInit
{
  @ViewChild('chart') chart?: ChartComponent;
  public chartOptions: Partial<ScatterChartOptions> = {};
  @Input() xAxisLabel?: string;
  @Input() yAxisLabel?: string;
  data = input.required<TimeIntervalDto>();
  chartReady = input.required<boolean>();

  ngOnInit(): void {}

  override onDropdownChange(): void {
    this.onFilterChange.emit(this.selectedFilters);
  }

 diagnosisChange = computed(() =>
  (this.data().changedDiagnosis ?? []).map(item => ({
    x: new Date(item.dateDischarged).getTime(),
    y: item.daysToNext,
    meta: { ...item },
  }))
);

diagnosisUnchange = computed(() =>
  (this.data().unchangedDiagnosis ?? []).map(item => ({
    x: new Date(item.dateDischarged).getTime(),
    y: item.daysToNext,
    meta: { ...item },
  }))
);

  constructor() {
    super();
    effect(() => {
      const datas = this.data();
      if (this.data()) {
        this.updateChartOptions();
      }
      
    });
  }

  private updateChartOptions() {
    this.chartOptions = {
      series: [
        {
          name: 'Changed Diagnosis',
          data: this.diagnosisChange(),
          color: '#dc3545',
        },
        {
          name: 'Unchanged Diagnosis',
          data: this.diagnosisUnchange(),
          color: '#007bff',
        },
      ],
      chart: {
        height: 350,
        type: 'scatter',
        animations: { enabled: false },
        zoom: {
          enabled: true,
          type: 'x',
          autoScaleYaxis: true,
          allowMouseWheelZoom: false,
          zoomedArea: {
            fill: { color: '#90CAF9', opacity: 0.4 },
            stroke: { color: '#0D47A1', opacity: 0.4, width: 1 },
          },
        },
        toolbar: {
          show: true,
          tools: { download: true, zoom: true, zoomin: false, zoomout: false, pan: false, reset: true },
        },
      },
      markers: {
        size: 6,
        colors: undefined,
        strokeColors: '#fff',
        strokeWidth: 1,
        hover: {
          size: 8,
        },
      },
      xaxis: {
        type: 'datetime',
        tickAmount: 10,
        title: {
          text: this.xAxisLabel,
        },
      },
      yaxis: {
        tickAmount: 7,
        show: true,
        title: {
          text: this.yAxisLabel,
        },
      },
      legend: {
        show: true,
      },
      fill: {
        type: 'solid',
        colors: undefined,
      },
      tooltip: {
        custom: function ({
          series,
          seriesIndex,
          dataPointIndex,
          w,
        }: {
          series: any;
          seriesIndex: number;
          dataPointIndex: number;
          w: any;
        }) {
          const dataPoint = w.config.series[seriesIndex].data[dataPointIndex];
          if (!dataPoint || !dataPoint.meta) return '';

          const meta = dataPoint.meta;
          return `
            <div style="padding:10px;">
              <strong>Discharged ICD10:</strong> ${meta.rootDischargedIcd10}<br/>
              <strong>Next Admission ICD10:</strong> ${meta.nextRootAdmissionIcd10}<br/>
              <strong>Diagnosis Changed:</strong> ${meta.diagnosisChanged}<br/>
              <strong>Date Discharged:</strong> ${new Date(dataPoint.x).toLocaleDateString()}<br/>
              <strong>Days to Next:</strong> ${dataPoint.y}
            </div>
          `;
        },
      },
    };
  }
}
